import React from 'react';
import { Card, Button } from '../../ui';
import { TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import GroupsTable from './GroupsTable';
import GroupsSearch from './GroupsSearch';
import Pagination from '../components/Pagination';
import GroupStats from '../components/GroupStats';

const GroupsManager = ({
  chats,
  selectedChats,
  groupsHandlers,
  groupsLoading,
  isNewProject,
  chatStats,
  pagination,
  onPageChange,
  onPageSizeChange,
  pageSize,
}) => {
  const {
    filters,
    pendingSearch,
    bulkRemoveMode,
    handleSearchChange,
    handleFilterChange,
    handleClearFilters,
    handleChatSelection,
    handleSelectAllChats,
    handleRemoveChat,
    handleOpenAddGroupsDialog,
    handleOpenBulkRemoveDialog,
  } = groupsHandlers;

  if (isNewProject) {
    return null; // Don't show groups manager for new projects
  }

  const bulkButtonLabel =
    bulkRemoveMode.mode === 'selected'
      ? `Remove Selected (${bulkRemoveMode.count})`
      : bulkRemoveMode.mode === 'filter'
        ? `Remove All Matching (${bulkRemoveMode.count})`
        : null;

  return (
    <Card>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-gray-900">Associated Groups</h2>
        <div className="flex gap-2">
          {bulkButtonLabel && (
            <Button
              variant="danger"
              icon={TrashIcon}
              onClick={handleOpenBulkRemoveDialog}
            >
              {bulkButtonLabel}
            </Button>
          )}
          <Button
            variant="primary"
            icon={PlusIcon}
            onClick={handleOpenAddGroupsDialog}
          >
            Add Groups
          </Button>
        </div>
      </div>

      {/* Groups Statistics */}
      <div className="mb-4">
        <GroupStats
          stats={chatStats}
          title="Project Groups"
          isLoading={groupsLoading && !chatStats}
        />
      </div>

      <GroupsSearch
        pendingSearch={pendingSearch}
        filters={filters}
        onSearchChange={handleSearchChange}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        resultCount={pagination?.totalCount ?? chats.length}
        totalCount={chatStats?.total_groups ?? pagination?.totalCount ?? chats.length}
      />

      <div className="mb-4 flex items-center justify-between gap-4">
        <div className="text-sm text-gray-600">
          {pagination ? `Page ${pagination.page} of ${pagination.totalPages}` : 'Loading groups...'}
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="project-groups-page-size" className="text-sm text-gray-600">
            Per page
          </label>
          <select
            id="project-groups-page-size"
            value={pageSize}
            onChange={onPageSizeChange}
            className="rounded-md border border-gray-300 px-2 py-1 text-sm"
          >
            {[100, 250, 500].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>

      <GroupsTable
        chats={chats}
        isLoading={groupsLoading}
        selectedChats={selectedChats}
        onChatSelection={handleChatSelection}
        onSelectAllChats={handleSelectAllChats}
        onRemoveChat={handleRemoveChat}
      />

      {/* Pagination */}
      {pagination && (
        <div className="mt-4">
          <Pagination
            pagination={pagination}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </Card>
  );
};

export default GroupsManager;